import { SkeletonPlaceholder } from "@expert/common-ui";
import { MantineColor, type SkeletonProps, Text, type TextProps } from "@mantine/core";

import type { ChatTask } from "@/sdk";

type CustomerNameProps = Pick<ChatTask, "customerName"> & {
    size?: string;
    truncate?: TextProps["truncate"] | { maxChars?: number };
    lineClamp?: number;
    color?: MantineColor;
};

export const CustomerName = ({
    customerName,
    size = "xs",
    truncate = "end",
    lineClamp,
    color,
    ...skeletonProps
}: CustomerNameProps & SkeletonProps) => {
    const rawValue = customerName.value;
    const isValidString = typeof rawValue === "string" && rawValue.length > 0;
    const hasValidName = !customerName.error && isValidString;
    const nameValue = isValidString ? rawValue : "";

    // Handle the maxChars truncation if specified
    const hasMaxChars = typeof truncate === "object" && truncate !== null && "maxChars" in truncate;
    const maxChars = hasMaxChars ? truncate.maxChars : undefined;

    // Truncate text based on maxChars if specified (only for valid names)
    let displayText = hasValidName ? nameValue : "Name unavailable";
    if (hasValidName && maxChars && nameValue.length > maxChars) {
        displayText = `${nameValue.substring(0, maxChars)}...`;
    }

    const textTruncate = hasMaxChars ? false : (truncate as TextProps["truncate"]);

    return (
        <SkeletonPlaceholder visible={customerName.loading} {...skeletonProps}>
            <Text
                span
                fw="bold"
                size={size}
                truncate={textTruncate}
                lineClamp={lineClamp}
                c={color}
                maw="100%"
                title={hasValidName ? nameValue : undefined} // Show full name on hover
            >
                {displayText}
            </Text>
        </SkeletonPlaceholder>
    );
};
